import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { Search, X } from "lucide-react";

/**
 * Autocomplete input for asset symbols. Searches /api/search and lets user pick a result.
 * Props:
 *   - value: current symbol string
 *   - onChange(symbol) — called as user types
 *   - onPick({ symbol, name, type }) — called when user selects a suggestion
 *   - placeholder
 */
export const AssetAutocomplete = ({ value, onChange, onPick, placeholder = "AAPL, BTC-USD…" }) => {
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!value || !value.trim()) { setResults([]); return undefined; }
        const ctrl = new AbortController();
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.get(`${API}/search`, {
                    params: { q: value, limit: 8 },
                    signal: ctrl.signal,
                });
                setResults(data);
                setActive(0);
            } catch (err) {
                if (err.name !== "CanceledError" && process.env.NODE_ENV !== "production") {
                    console.warn("Autocomplete search failed:", err);
                }
            }
        }, 150);
        return () => { clearTimeout(t); ctrl.abort(); };
    }, [value]);

    useEffect(() => {
        const onDoc = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const pick = (item) => {
        onChange(item.symbol);
        onPick(item);
        setOpen(false);
    };

    const onKey = (e) => {
        if (!open || !results.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive(a => Math.min(a + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive(a => Math.max(a - 1, 0));
        } else if (e.key === "Enter" && results[active]) {
            e.preventDefault();
            pick(results[active]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} className="relative">
            <div className="flex items-center gap-1 bg-elev2 border border-soft px-2 py-1.5 focus-within:border-amber">
                <Search size={11} className="text-mute" />
                <input
                    data-testid="autocomplete-input"
                    value={value}
                    onChange={(e) => { onChange(e.target.value.toUpperCase()); setOpen(true); }}
                    onFocus={() => value && setOpen(true)}
                    onKeyDown={onKey}
                    placeholder={placeholder}
                    className="bg-transparent outline-none text-sm flex-1 placeholder:text-mute font-mono"
                    autoComplete="off"
                />
                {value && (
                    <button type="button" onClick={() => onChange("")} className="text-mute hover:text-foreground">
                        <X size={10} />
                    </button>
                )}
            </div>
            {open && results.length > 0 && (
                <div data-testid="autocomplete-results" className="absolute top-full left-0 right-0 mt-1 border border-strong bg-elev shadow-xl z-50 max-h-72 overflow-y-auto">
                    {results.map((r, i) => (
                        <button
                            key={r.symbol}
                            type="button"
                            data-testid={`autocomplete-result-${r.symbol}`}
                            onMouseEnter={() => setActive(i)}
                            onClick={() => pick(r)}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left ${active === i ? "bg-elev2" : ""} hover:bg-elev2`}
                        >
                            <span className="font-semibold text-amber tnum w-20 truncate">{r.symbol}</span>
                            <span className="text-dim flex-1 truncate">{r.name}</span>
                            <span className="text-[9px] text-mute uppercase">{r.type}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
